import { supabase } from './supabase';

export interface ListingData {
  restaurant_id?: string;
  bar_id?: string;
  date: string;
  time?: string;
  party_size: number;
  preferences?: string;
  description?: string;
  location_lat?: number;
  location_lng?: number;
}

export interface Listing {
  id: string;
  user_id: string;
  restaurant_id?: string;
  bar_id?: string;
  date: string;
  meeting_time?: string;
  party_size: number;
  preferences?: string;
  description?: string;
  location_lat?: number;
  location_lng?: number;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_profile_picture?: string;
  venue_name?: string;
  venue_type?: 'restaurant' | 'bar';
  venue_address?: string;
  venue_cuisine_type?: string;
  venue_bar_type?: string;
  venue_price_range?: string;
  distance?: number;
}

export const createListing = async (data: ListingData): Promise<Listing> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to create a listing');

    // Create the listing
    const { data: listing, error: listingError } = await supabase
      .from('listings')
      .insert({
        user_id: user.id,
        restaurant_id: data.restaurant_id,
        bar_id: data.bar_id,
        date: data.date,
        meeting_time: data.time,
        party_size: data.party_size,
        preferences: data.preferences,
        description: data.description,
        location_lat: data.location_lat,
        location_lng: data.location_lng
      })
      .select()
      .single();

    if (listingError) throw listingError;
    return listing;
  } catch (err) {
    console.error('Error creating listing:', err);
    throw err;
  }
};

export const getNearbyListings = async (
  lat: number,
  lng: number,
  radiusMiles: number = 50
): Promise<Listing[]> => {
  try {
    const { data, error } = await supabase.rpc('get_nearby_listings', {
      user_lat: lat,
      user_lng: lng,
      radius_miles: radiusMiles
    });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error fetching nearby listings:', err);
    throw err;
  }
};

export const getUserListings = async (): Promise<Listing[]> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('You must be logged in to view your listings');

    const { data, error } = await supabase
      .from('listings')
      .select(`
        *,
        profile:profiles(full_name, profile_picture),
        restaurant:restaurants(name, address, cuisine_type, price_range),
        bar:bars(name, address, bar_type, price_range)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform the data to match the Listing interface
    return (data || []).map(item => ({
      ...item,
      user_name: item.profile?.full_name,
      user_profile_picture: item.profile?.profile_picture,
      venue_name: item.restaurant?.name || item.bar?.name,
      venue_type: item.restaurant ? 'restaurant' : 'bar',
      venue_address: item.restaurant?.address || item.bar?.address,
      venue_cuisine_type: item.restaurant?.cuisine_type,
      venue_bar_type: item.bar?.bar_type,
      venue_price_range: item.restaurant?.price_range || item.bar?.price_range
    }));
  } catch (err) {
    console.error('Error fetching user listings:', err);
    throw err;
  }
};

export const deleteListing = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('listings')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (err) {
    console.error('Error deleting listing:', err);
    throw err;
  }
};